import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import { ReactComponent as GlobeIcon } from "../../../assets/ic_globe.svg";
import {
  CreateStateRequest,
  State,
  UpdateStateRequest,
} from "../../../api/services/state/type";
import CustomInput from "../../ui/CustomInput";
import PrimaryButton from "../../ui/PrimaryButton";

interface StateFormProps {
  state?: State;
  countryName: string;
  isLoading?: boolean;
  onSubmit: (data: CreateStateRequest | UpdateStateRequest) => void;
  onCancel: () => void;
}

interface StateFormData {
  name: string;
}

export const StateForm: React.FC<StateFormProps> = ({
  state,

  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isEditMode = !!state;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<StateFormData>({
    defaultValues: {
      name: state?.name || "",
    },
  });

  React.useEffect(() => {
    if (state) {
      setValue("name", state.name);
    } else {
      reset();
    }
  }, [state, setValue, reset]);

  const onFormSubmit = (data: StateFormData) => {
    onSubmit(data as CreateStateRequest | UpdateStateRequest);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? "Edit State" : "Add New State"}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            <CustomInput
              label="State Name"
              type="text"
              placeholder="Enter state name"
              register={register}
              name="name"
              validation={{
                required: "State name is required",
                validate: (v: string) => {
                  const trimmed = v.trim();
                  if (trimmed.length === 0) return "State name is required";
                  if (trimmed.length < 2)
                    return "State name must be at least 2 characters";
                  if (!/^[A-Za-z\s]+$/.test(trimmed))
                    return "State name must contain letters and spaces only";
                },
              }}
              error={errors.name}
              disabled={isLoading}
              icon={<GlobeIcon className="w-5 h-5 text-slate-400" />}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              className="flex-1"
            >
              {isEditMode ? "Update State" : "Create State"}
            </PrimaryButton>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
